import {
  action, computed, observable,
} from 'mobx';
import { RouterStore } from '@estee/elc-router';
import Config, { IAddressDisplaySchema } from '../../domain/entities/Config/Config';

import ShippingRepository from '../../repositories/ShippingRepository';
import BillingRepository from '../../repositories/BillingRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import Order from '../../domain/entities/Order/Order';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../constants/PaymentProviders';
import ConfigRepository from '../../repositories/ConfigRepository';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';

import CartItem from '../../domain/entities/Cart/CartItem';
import { formatPhone } from '../../utils/AddressFormUtils';
import Offer from '../../domain/entities/Offer/Offer';
import Address from '../../domain/entities/Address';
import Transaction from '../../domain/entities/Transaction/Transaction';
import Cart from '../../domain/entities/Cart/Cart';
import { DELIVERY } from '../../routes';
import formatPrice from '../../utils/PriceFormatUtils';
import Bopis from '../../domain/entities/Bopis/Bopis';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import BopisStoresRepository from '../../repositories/BopisStoresRepository';
import CompanyBillingDetails from '../../domain/entities/CompanyBillingDetails';
import { IShippingMethod } from '../../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';
import { DeliveryOptions, FIELD_TYPE } from '../../constants/Constants';
import Store from '../../domain/entities/Store/Store';
import ConvenienceStore from '../../domain/entities/ConvenienceStore/ConvenienceStore';
import { IAfterpayLimis } from '../../domain/entities/Payment/Payment';
import Routes, { ExternalRoute } from '../../application/routes/Routes';
import { IRetailFeeTranslations, RETAIL_FEE_REGION, RETAIL_FEE_REGION_TRANSLATIONS_MAP } from '../../constants/RetailDeliveryFeeMappedTranslations';
import TranslationRepository from '../../repositories/TranslationRepository';

interface IOrderSummaryConfig {
  shippingAddressRepository: ShippingRepository;
  bopisStoresRepository: BopisStoresRepository;
  billingAddressRepository: BillingRepository;
  transactionRepository: TransactionRepository;
  configRepository: ConfigRepository;
  stateRouter: RouterStore;
  analyticsRepository: AnalyticsRepository;
  translationRepository: TranslationRepository;
  routes: Routes;
}

interface IOrderSummaryViewControllerData {
  afterpayLimits: IAfterpayLimis;
  afterpayExcludedProducts: boolean;
  allowedOffers: Offer[];
  billingAddress: Address;
  bopis: Bopis;
  convenienceStore: ConvenienceStore;
  cartItems: CartItem[];
  countryId: number;
  currency: string;
  donationItems: CartItem[];
  firstManualOffer: Offer;
  isGiftWrapMessageEmpty: boolean;
  isGiftWrapSelected: boolean;
  isGiftWrapEnabled: boolean;
  isBopisSelected: boolean;
  inEditMode: boolean;
  isTotalEstimated: boolean;
  locale: string;
  loyaltyPoints: number | string;
  orderSummary: Order;
  selectedShippingMethodId: SHIPPING_METHODS;
  shippingAddress: Address;
  shippingMethods: IShippingMethod[];
  showCountryLabel: boolean;
  showSalesTax: boolean;
  config: Config;
  vatPercentage: number;
  sameDayDeliveryPrice?: string;
  email: string;
  isUserSignedIn: boolean;
  displayIncludedTax: boolean;
  countryTaxesLabel: boolean;
  shippingAddressDisplaySchema: IAddressDisplaySchema | {};
  billingAddressDisplaySchema: IAddressDisplaySchema | {};
  showLoyaltyPoints: boolean;
  companyBillingDetails: CompanyBillingDetails;
  companyBillingDisplaySchema: IAddressDisplaySchema;
  isTaxDetermined: boolean;
  totalAppliedTax: string;
  showFreeLabelAsPrice: boolean;
  rewardPointsPriceInCartFormatted: string;
  selectedStore: Store;
  klarnaInstallmentsNumber: number;
  cartUrl: string;
  signOutUrl: string;
  retailFeeTranslations: IRetailFeeTranslations;
}

export type IGetPriceOptions = {
  freeLabel: string;
  showFreeLabelAsPrice: boolean;
};

interface IOrderSummaryTranslations {
  [key: string]: string;
}

export default class OrderSummaryViewController {
  shippingAddressRepository: ShippingRepository;

  billingAddressRepository: BillingRepository;

  transactionRepository: TransactionRepository;

  configRepository: ConfigRepository;

  bopisStoresRepository: BopisStoresRepository;

  private analyticsRepository: AnalyticsRepository;

  private translationRepository: TranslationRepository;

  @observable stateRouter: RouterStore;

  @observable config: Config;

  @observable cart: Cart;

  @observable transaction: Transaction;

  @observable email: string;

  @observable isUserSignedIn: boolean;

  private routes: Routes;

  constructor(config: IOrderSummaryConfig) {
    this.shippingAddressRepository = config.shippingAddressRepository;
    this.billingAddressRepository = config.billingAddressRepository;
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.bopisStoresRepository = config.bopisStoresRepository;
    this.stateRouter = config.stateRouter;
    this.analyticsRepository = config.analyticsRepository;
    this.translationRepository = config.translationRepository;
    this.routes = config.routes;
  }

  @action
  async loaded(): Promise<void> {
    const config = await this.configRepository.getConfig();
    const transaction = await this.transactionRepository.getTransaction();
    const {
      user: {
        email,
        isUserRegistered,
      },
    } = transaction;
    this.setConfig(config);
    this.setTransaction(transaction);
    this.setEmail(email);
    this.setIsUserSignedIn(isUserRegistered);
    const { bopis } = this.transaction || {};
    if (bopis?.doorId) {
      const { loadBobisStore } = this.bopisStoresRepository;
      loadBobisStore(bopis.doorId);
    }
  }

  @action
  setConfig = (config: Config): void => {
    this.config = config;
  };

  @action
  setTransaction = (transaction: Transaction): void => {
    this.transaction = transaction;
  };

  @action
  setEmail = (email: string): void => {
    this.email = email;
  };

  @action
  setIsUserSignedIn = (isUserSignedIn: boolean): void => {
    this.isUserSignedIn = isUserSignedIn;
  };

  onEditStore = (): void => {
    this.analyticsRepository.editDeliveryFromPayment();
  };

  onEditConvenienceStore = (): void => {
    this.analyticsRepository.editDeliveryFromPayment();
    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE);
  };

  @computed
  get data(): IOrderSummaryViewControllerData {
    const defaultKlarnaInstallmentsNumber = 3;
    return {
      afterpayExcludedProducts: this.transaction?.afterpayExcludedProducts,
      afterpayLimits: this.transaction?.payment.afterPayLimits,
      allowedOffers: this.allowedOffers,
      billingAddress: this.transaction && this.billingAddress,
      billingAddressDisplaySchema: this.config ? this.config.billingAddressDisplaySchema : {},
      bopis: this.transaction?.isBopisOrder && this.transaction.bopis,
      cartItems: this.cartItems,
      cartUrl: this.routes.getExternalRoute(ExternalRoute.CART),
      companyBillingDetails: this.transaction?.companyBillingDetails,
      companyBillingDisplaySchema: this.config ? this.config.companyBillingDisplaySchema : {},
      config: this.config,
      convenienceStore: this.transaction?.convenienceStore,
      countryId: this.config?.countryId,
      countryTaxesLabel: this.config?.countryTaxesLabel,
      currency: this.config?.currency,
      displayIncludedTax: this.config?.displayIncludedTax,
      donationItems: this.transaction?.cart.donationItems,
      email: this.email,
      firstManualOffer: this.firstManualOffer,
      inEditMode: this.inEditMode,
      isBopisSelected: this.bopisStoresRepository.isBopisSelected,
      isGiftWrapEnabled: !!this.transaction?.giftWrap && this.transaction?.giftWrapSettings?.giftWrapEnabled,
      isGiftWrapMessageEmpty: !this.transaction?.cardMessage,
      isGiftWrapSelected: !!this.transaction?.giftWrap,
      isTaxDetermined: this.isTaxDetermined,
      isTotalEstimated: this.isTotalEstimated,
      isUserSignedIn: this.isUserSignedIn,
      klarnaInstallmentsNumber: this.transaction?.payment?.klarnaInstallmentsNumber || defaultKlarnaInstallmentsNumber,
      locale: this.config?.locale,
      loyaltyPoints: this.transaction?.loyaltyPoints,
      orderSummary: this.transaction?.orderSummary,
      retailFeeTranslations: this.retailFeeTranslations,
      rewardPointsPriceInCartFormatted: this.transaction?.orderSummary?.rewardPointsPriceInCartFormatted,
      sameDayDeliveryPrice: this.getPrice(this.sameDayDeliveryPrice),
      selectedShippingMethodId: this.transaction?.selectedShippingMethodId,
      selectedStore: this.selectedStore,
      shippingAddress: this.transaction && this.shippingAddress,
      shippingAddressDisplaySchema: this.config ? this.config.shippingAddressDisplaySchema : {},
      shippingMethods: this.transaction?.shippingMethods.items,
      showCountryLabel: this.showCountryLabel,
      showFreeLabelAsPrice: this.config?.showFreeLabelAsPrice,
      showLoyaltyPoints: this.showLoyaltyPoints,
      showSalesTax: this.salesTax,
      signOutUrl: this.routes.getExternalRoute(ExternalRoute.SIGNED_OUT),
      totalAppliedTax: this.totalAppliedTax,
      vatPercentage: this.config?.vatPercentage,
    };
  }


  @computed
  get totalAppliedTax(): string {
    return this.getPrice(this.isTaxDetermined && this.transaction
      ? this.transaction.orderSummary.totalAppliedTax : 0);
  }

  @computed
  get isTaxDetermined(): boolean {
    const { transaction } = this;

    return transaction?.isSalesTaxReady && !this.inEditMode;
  }

  @computed
  get inEditMode(): boolean {
    return Boolean(Number(this.stateRouter.queryParams.inEditMode));
  }

  @computed
  get sameDayDeliveryPrice(): number {
    const priceFromOrderSummary = this.transaction?.orderSummary?.totalShippingPrice;
    const priceFromShipMethods = this.transaction?.shippingOptions?.getMinimumPriceByDelivery(DeliveryOptions.SAME_DAY_DELIVERY);
    return priceFromOrderSummary ?? priceFromShipMethods;
  }


  @computed
  get isTotalEstimated(): boolean {
    if (this.transaction?.user) {
      if (this.transaction.user.isUserRegistered) {
        return this.transaction.shippingAddress.isNewAddress
          || Boolean(Number(this.stateRouter.queryParams.inEditMode));
      }
      return this.stateRouter.currentRoute === DELIVERY.ROUTE;
    }

    return true;
  }

  @computed
  get allowedOffers(): Offer[] {
    return (this.transaction?.offers || []).filter(offer => offer.isAllowed);
  }

  @computed
  get firstManualOffer(): Offer {
    return this.transaction?.offers.find((offer: Offer) => offer.activation === 'manual');
  }

  @computed
  private get showCountryLabel(): boolean {
    return this.config && this.config.shippingCountries.length > 1;
  }

  getPrice = (price: number, options?: IGetPriceOptions): string => {
    if (options?.showFreeLabelAsPrice && price === 0) {
      return options.freeLabel;
    }
    return this.config && formatPrice(this.config, price);
  };

  @computed
  get shippingAddress(): Address {
    const shippingAddress = this.transaction?.shippingAddress;
    const {
      [FIELD_TYPE.PHONE]: mobileNumber,
      [FIELD_TYPE.COUNTRY_ID]: countryId,
    } = shippingAddress.attributes;

    if (!mobileNumber.startsWith('+')) {
      return Address.fromJSON({
        ...shippingAddress.attributes,
        [FIELD_TYPE.ID]: shippingAddress.id,
        [FIELD_TYPE.PHONE]: formatPhone(mobileNumber, countryId),
      });
    }

    return shippingAddress;
  }

  @computed
  get billingAddress(): Address {
    const billingAddress = this.transaction?.billingAddress;
    const {
      [FIELD_TYPE.PHONE]: mobileNumber,
      [FIELD_TYPE.COUNTRY_ID]: countryId,
    } = billingAddress.attributes;

    if (!mobileNumber.startsWith('+')) {
      return Address.fromJSON({
        ...billingAddress.attributes,
        [FIELD_TYPE.ID]: billingAddress.id,
        [FIELD_TYPE.PHONE]: formatPhone(mobileNumber, countryId),
      });
    }

    return billingAddress;
  }

  @computed
  private get salesTax(): boolean {
    const { vatPercentage } = this.configRepository.config;
    return !vatPercentage;
  }

  @computed
  get isAfterPayAvailable(): boolean {
    return this.transaction?.payment?.paymentProviders
      .some(p => p.providerName === PAYMENT_PROVIDERS.AFTERPAY);
  }

  @computed
  get isShippingSameCountryAsConfig(): boolean {
    return this.shippingAddressRepository.formStore.values[FIELD_TYPE.COUNTRY_ID] === this.config.countryId;
  }

  @computed
  get isBillingSameCountryAsConfig(): boolean {
    return this.billingAddressRepository.formStore.values[FIELD_TYPE.COUNTRY_ID] === this.config.countryId;
  }

  /**
   * Klarna message is available in order summary when
   * Klarna payment method is available in transaction
   */
  @computed
  get isKlarnaAvailable(): boolean {
    const isKlarnaAvailableInTrans = this.transaction?.payment?.paymentProviders
      .some(p => p.isAvailable &&
        (p.paymentMode === PAYMENT_MODES.ADYEN_WEB_KLARNA || p.paymentMode === PAYMENT_MODES.ADYEN_WEB_KLARNA_ACCOUNT));

    return isKlarnaAvailableInTrans;
  }

  @computed
  get isPayPayAvailable(): boolean {
    return this.transaction?.payment?.paymentProviders
      .some(p => p.isAvailable && (p.paymentMode === PAYMENT_MODES.ADYEN_WEB_PAYPAY ));
  }

  @computed
  get isRakutenPayAvailable(): boolean {
    return this.transaction?.payment?.paymentProviders
      .some(p => p.isAvailable && (p.paymentMode === PAYMENT_MODES.ADYEN_WEB_RAKUTENPAY ));
  }

  @computed
  get isAfterpayBillingEligible(): boolean {
    const billingCountry = this.transaction?.billingAddress.attributes[FIELD_TYPE.COUNTRY_ID];
    if (billingCountry) {
      return billingCountry === this.config?.countryId;
    }

    return true;
  }

  @computed
  public get cartItems(): CartItem[] {
    return this.transaction?.cart.cartItems;
  }

  @computed
  public get showLoyaltyPoints(): boolean {
    const loyaltyPoints = this.transaction?.loyaltyPoints;
    const hasLoyaltyEnabled = this.transaction?.user.hasLoyaltyEnabled;
    return Boolean(loyaltyPoints) && hasLoyaltyEnabled;
  }

  @computed
  private get selectedStore(): Store {
    const { getSelectedStore, selectedStore } = this.bopisStoresRepository.bopisStoresCollection;
    if (this.transaction?.bopis.doorId) {
      return (
        getSelectedStore(this.transaction.bopis.doorId)
        || selectedStore
      );
    }
    if (this.bopisStoresRepository.bopisFormData.doorId) {
      return getSelectedStore(this.bopisStoresRepository.bopisFormData.doorId);
    }
    return undefined;
  }

  @computed
  private get retailFeeTranslations(): IRetailFeeTranslations {
    const feeRegion: RETAIL_FEE_REGION = this.transaction?.orderSummary?.retailDeliveryFeeMessage;
    if (feeRegion) {
      const translations = this.translationRepository?.
        listRequiredTranslationSchema(Object.values(RETAIL_FEE_REGION_TRANSLATIONS_MAP[feeRegion])) as IOrderSummaryTranslations;

      const regionTranslations = RETAIL_FEE_REGION_TRANSLATIONS_MAP[feeRegion];

      return {
        label: translations?.[regionTranslations?.label],
        message: translations?.[regionTranslations?.message],
      };
    }
  }
}
